import { useState, useEffect, useRef, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import LoginPage from "@/components/LoginPage";
import Sidebar from "@/components/Sidebar";
import EmailPreview from "@/components/EmailPreview";
import SellsyStatusBanner from "@/components/SellsyStatusBanner";
import PhoneConflictDialog from "@/components/PhoneConflictDialog";
import { Loader2 } from "lucide-react";
import useOutlookEmail from "@/hooks/useOutlookEmail";
import usePopoutBridge from "@/hooks/usePopoutBridge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios to send cookies
axios.defaults.withCredentials = true;

const POPOUT_STATE_KEY = "mailsync-popout-state";
const isPopout = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("popout") === "1";

// Main App Content (protected)
function AppContent() {
  const { user, loading: authLoading } = useAuth();

  // When opened as a pop-out, hydrate from localStorage (written by the task pane)
  const initialState = (() => {
    if (!isPopout) return null;
    try {
      const raw = localStorage.getItem(POPOUT_STATE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (_) {
      return null;
    }
  })();

  const [email, setEmail] = useState(initialState?.email ?? null);
  const [rawEmailText, setRawEmailText] = useState(initialState?.rawEmailText ?? '');
  const [signatureImageB64, setSignatureImageB64] = useState(initialState?.signatureImageB64 ?? null);
  const [signatureImagesB64, setSignatureImagesB64] = useState(initialState?.signatureImagesB64 ?? []);
  const [signatureImageUrls, setSignatureImageUrls] = useState(initialState?.signatureImageUrls ?? []);
  const [extractedData, setExtractedData] = useState(initialState?.extractedData ?? null);
  const [loading, setLoading] = useState(false);
  const [crmStatus, setCrmStatus] = useState(initialState?.crmStatus ?? { contact: null, company: null });
  const [animations, setAnimations] = useState([]);
  const [templates, setTemplates] = useState([]);
  // Persisted creations (cleared only on new email extraction)
  const [createdContact, setCreatedContact] = useState(initialState?.createdContact ?? null);
  const [createdCompany, setCreatedCompany] = useState(initialState?.createdCompany ?? null);
  const [createdOpportunity, setCreatedOpportunity] = useState(initialState?.createdOpportunity ?? null);
  const [createdQuote, setCreatedQuote] = useState(initialState?.createdQuote ?? null);

  // Phone conflict dialog state (populated when Sellsy already has a different phone)
  const [phoneConflict, setPhoneConflict] = useState(null);
  // phoneConflict shape when active:
  //   { contactId, contactName, conflicts: [{ field, existing, new, sellsy_field }], pendingExtracted }

  // Mirror state to localStorage so a popout can pick it up when opened
  useEffect(() => {
    if (isPopout) return; // only the task pane writes
    try {
      const snapshot = {
        email, rawEmailText, signatureImageB64, signatureImagesB64, signatureImageUrls, extractedData, crmStatus,
        createdContact, createdCompany, createdOpportunity, createdQuote,
        savedAt: Date.now(),
      };
      localStorage.setItem(POPOUT_STATE_KEY, JSON.stringify(snapshot));
    } catch (_) {
      // Quota exceeded — skip
    }
  }, [email, rawEmailText, signatureImageB64, signatureImagesB64, signatureImageUrls, extractedData, crmStatus, createdContact, createdCompany, createdOpportunity, createdQuote]);

  // Seed data on first load (only when authenticated)
  useEffect(() => {
    if (!user) return;
    
    const seedData = async () => {
      try {
        await axios.post(`${API}/seed/templates`);
      } catch (e) {
        console.log("Data already seeded or error:", e.message);
      }
    };
    seedData();
  }, [user]);

  // Fetch the live Sellsy catalog (grouped by base animation name) + templates.
  // Independent calls so a Sellsy outage doesn't blank out the templates list.
  useEffect(() => {
    if (!user) return;

    axios.get(`${API}/sellsy/catalog/animations`)
      .then((res) => setAnimations(res.data?.animations || []))
      .catch((e) => console.error("Sellsy catalog fetch failed:", e));

    axios.get(`${API}/email-templates`)
      .then((res) => setTemplates(res.data))
      .catch((e) => console.error("Email templates fetch failed:", e));
  }, [user]);

  // Outlook add-in: auto-fill email content when running inside Outlook task pane.
  // The hook calls onLoaded with the FRESH values (body + signature) when both are
  // ready (or after timeout), avoiding setState/useEffect race conditions.
  const handleExtractRef = useRef(null);
  const userRef = useRef(user);
  userRef.current = user;

  // Ref always pointing to the latest snapshot — used by openPopout to push state
  const stateSnapshotRef = useRef({});
  stateSnapshotRef.current = {
    email, rawEmailText, signatureImageB64, signatureImagesB64, signatureImageUrls, extractedData, crmStatus,
    createdContact, createdCompany, createdOpportunity, createdQuote,
  };

  const getStateForPopout = useCallback(() => stateSnapshotRef.current, []);

  const { isOutlookContext, reload: reloadOutlookEmail, openPopout: openOutlookPopout } = useOutlookEmail({
    setRawEmailText,
    setSignatureImageB64,
    setSignatureImagesB64,
    setSignatureImageUrls,
    onLoaded: () => {
      // Email content is auto-loaded into the textarea, but extraction
      // happens only when the user clicks "Analyser l'email"
    },
    getStateForPopout,
    disabled: isPopout,
  });

  // Popout hydration: when running as a popout, ask parent for state via Office messaging
  const popoutHydrated = useRef(false);
  const handlePopoutState = useCallback((payload) => {
    if (popoutHydrated.current) return; // hydrate ONCE — avoid loops
    popoutHydrated.current = true;
    if (payload.email !== undefined) setEmail(payload.email);
    if (payload.rawEmailText !== undefined) setRawEmailText(payload.rawEmailText);
    if (payload.signatureImageB64 !== undefined) setSignatureImageB64(payload.signatureImageB64);
    if (payload.signatureImagesB64 !== undefined) setSignatureImagesB64(payload.signatureImagesB64);
    if (payload.signatureImageUrls !== undefined) setSignatureImageUrls(payload.signatureImageUrls);
    if (payload.extractedData !== undefined) setExtractedData(payload.extractedData);
    if (payload.crmStatus !== undefined) setCrmStatus(payload.crmStatus);
    if (payload.createdContact !== undefined) setCreatedContact(payload.createdContact);
    if (payload.createdCompany !== undefined) setCreatedCompany(payload.createdCompany);
    if (payload.createdOpportunity !== undefined) setCreatedOpportunity(payload.createdOpportunity);
    if (payload.createdQuote !== undefined) setCreatedQuote(payload.createdQuote);
    toast.success("État synchronisé depuis l'extension");
  }, []);
  usePopoutBridge({
    enabled: isPopout,
    onState: handlePopoutState,
  });

  // Parse pasted email text to extract metadata
  const parseRawEmail = (text) => {
    const lines = text.split('\n');
    let subject = '';
    let senderEmail = '';
    let senderName = '';
    let bodyStart = 0;
    // Only treat the pre-blank-line block as email headers if we actually saw
    // header lines (Objet/De/From/Date). Otherwise (e.g. the "Trame de demande"
    // structured format), keep the FULL text as body so nothing is dropped.
    let foundHeader = false;

    for (let i = 0; i < Math.min(lines.length, 15); i++) {
      const line = lines[i].trim();
      // Subject
      if (line.toLowerCase().startsWith('objet') || line.toLowerCase().startsWith('subject')) {
        subject = line.replace(/^(objet|subject)\s*:\s*/i, '').trim();
        foundHeader = true;
      }
      // Sender email
      const emailMatch = line.match(/[\w\.\-\+]+@[\w\.\-]+\.\w+/);
      if (emailMatch && !senderEmail && !line.toLowerCase().includes('ari@azefir')) {
        senderEmail = emailMatch[0];
      }
      // From / De
      if (line.toLowerCase().startsWith('de ') || line.toLowerCase().startsWith('from')) {
        const nameMatch = line.replace(/^(de|from)\s*:\s*/i, '').trim();
        if (nameMatch && !nameMatch.includes('@')) {
          senderName = nameMatch;
        }
        foundHeader = true;
      }
      // Date header
      if (line.toLowerCase().startsWith('date')) {
        foundHeader = true;
      }
      // Look for first empty line as body start — ONLY when real headers exist
      if (line === '' && i > 2 && foundHeader) {
        bodyStart = i + 1;
        break;
      }
      if (line.toLowerCase().startsWith('bonjour') || line.toLowerCase().startsWith('cher') || line.toLowerCase().startsWith('madame')) {
        bodyStart = i;
        break;
      }
    }

    const body = lines.slice(bodyStart).join('\n').trim() || text;

    return {
      id: `email-${Date.now()}`,
      sender_email: senderEmail || 'inconnu@email.com',
      sender_name: senderName || senderEmail?.split('@')[0] || 'Expediteur',
      subject: subject || 'Demande client',
      body: body,
      received_at: new Date().toISOString()
    };
  };

  // Handle image paste in the signature area
  const handleImagePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const blob = item.getAsFile();
        const reader = new FileReader();
        reader.onload = () => {
          const b64 = reader.result.split(',')[1];
          setSignatureImageB64(b64);
          toast.success("Image de signature collee");
        };
        reader.readAsDataURL(blob);
        return;
      }
    }
  };

  // Insert a blank "request skeleton" into the email zone listing every field
  // title from the Aperçu tab — standalone window only (manual entry helper).
  const handleInsertTrame = () => {
    const trame = `CONTACT
- Prénom et nom : 
- Fonction : 
- Email : 
- Portable : 
- Fixe : 
- Activité client : 

SOCIÉTÉ / CLIENT
- Société : 
- Adresse : 
- Code postal : 
- Ville : 

ÉVÉNEMENT
- Date début événement : 
- Date fin événement : 
- Horaires : 
- Lieu événement : 
- Nombre de Participants : 
- Budget : 

ANIMATIONS DEMANDÉES
- 

REMARQUES : 
`;
    setRawEmailText((prev) => (prev && prev.trim() ? prev.trimEnd() + "\n\n" + trame : trame));
    toast.success("Trame de demande insérée");
  };

  // Extract email data — overrides allow passing fresh values that bypass React state
  // (used by the Outlook auto-load to avoid setState/useEffect race conditions)
  const handleExtractEmail = async (overrides = {}) => {
    const text = (overrides.rawEmailText ?? rawEmailText) || "";
    const sigB64 = overrides.signatureImageB64 !== undefined
      ? overrides.signatureImageB64
      : signatureImageB64;
    const sigImages = overrides.signatureImagesB64 !== undefined
      ? overrides.signatureImagesB64
      : signatureImagesB64;
    const sigUrls = overrides.signatureImageUrls !== undefined
      ? overrides.signatureImageUrls
      : signatureImageUrls;

    if (!text.trim()) {
      toast.error("Collez d'abord le contenu de l'email");
      return;
    }

    const parsedEmail = parseRawEmail(text);
    setEmail(parsedEmail);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/extract-email`, {
        id: parsedEmail.id,
        sender_email: parsedEmail.sender_email,
        sender_name: parsedEmail.sender_name,
        subject: parsedEmail.subject,
        body: parsedEmail.body,
        received_at: parsedEmail.received_at,
        signature_image_b64: sigB64 || null,
        signature_images_b64: (sigImages && sigImages.length) ? sigImages : null,
        signature_image_urls: (sigUrls && sigUrls.length) ? sigUrls : null,
      });
      setExtractedData(response.data);
      // Reset all persisted creations — fresh email
      setCreatedContact(null);
      setCreatedCompany(null);
      setCreatedOpportunity(null);
      setCreatedQuote(null);      toast.success("Donnees extraites avec succes");

      // Search for existing contact and company
      await searchCRM(response.data);
    } catch (e) {
      console.error("Extraction error:", e);
      toast.error("Erreur lors de l'extraction");
    } finally {
      setLoading(false);
    }
  };

  // Keep a stable ref to the latest handleExtractEmail so the Outlook hook
  // callback can invoke it without depending on closure freshness
  handleExtractRef.current = handleExtractEmail;

  // Search CRM for matches
  const searchCRM = async (data) => {
    try {
      const promises = [];
      
      if (data.email) {
        promises.push(
          axios.get(`${API}/sellsy/search/contact`, { params: { email: data.email } })
            .then(res => ({ type: 'contact', results: res.data.results }))
            .catch(() => ({ type: 'contact', results: [] }))
        );
      }
      
      if (data.company) {
        promises.push(
          axios.get(`${API}/sellsy/search/company`, { params: { name: data.company } })
            .then(res => ({ type: 'company', results: res.data.results }))
            .catch(() => ({ type: 'company', results: [] }))
        );
      }
      
      const results = await Promise.all(promises);
      
      const newStatus = { contact: null, company: null };
      results.forEach(r => {
        if (r.results.length > 0) {
          // Sort by match_score descending, then pick best match
          const sorted = [...r.results].sort((a, b) => (b.match_score || 0) - (a.match_score || 0));
          const bestMatch = sorted[0];
          if (bestMatch && (bestMatch.match_score || 0) >= 0.9) {
            newStatus[r.type] = { ...bestMatch, source: bestMatch.sellsy_id ? 'sellsy' : 'local' };
          }
          // If no high-score match, leave as null (new contact/company)
        }
      });
      setCrmStatus(newStatus);

      // Auto-attach address to an existing Sellsy company when we just detected
      // it AND the extracted data carries postal info. Idempotent — the backend
      // does nothing if the company already has any address.
      const companyMatch = newStatus.company;
      const hasAddrData = !!(data?.address || data?.postal_code || data?.city);
      if (companyMatch?.sellsy_id && hasAddrData) {
        try {
          const addrRes = await axios.post(
            `${API}/companies/${companyMatch.sellsy_id}/ensure-address`,
            {
              address: data?.address || null,
              postal_code: data?.postal_code || null,
              city: data?.city || null,
            }
          );
          if (addrRes.data?.status === "created") {
            toast.success(`Adresse ajoutée à la société "${companyMatch.name}" dans Sellsy`);
          }
          // 'already_present' or 'skipped' → silent
        } catch (_) {
          // Silent: address sync is best-effort
        }
      }

      // Auto-enrich an existing Sellsy contact with any extracted info missing
      // from the existing record (mobile/landline phone, job title). Phone
      // conflicts trigger a confirmation dialog rather than silent overwrite.
      const contactMatch = newStatus.contact;
      const hasContactEnrichData = !!(data?.mobile_phone || data?.landline_phone || data?.job_title);
      if (contactMatch?.sellsy_id && hasContactEnrichData) {
        try {
          const enrichRes = await axios.post(
            `${API}/contacts/${contactMatch.sellsy_id}/enrich`,
            {
              mobile_phone: data?.mobile_phone || null,
              landline_phone: data?.landline_phone || null,
              job_title: data?.job_title || null,
            }
          );
          const r = enrichRes.data;
          if (r?.status === "updated" && r?.updated_fields?.length) {
            const labels = r.updated_fields.map(f => ({
              mobile_number: "Portable",
              phone_number: "Fixe",
              position: "Fonction",
            }[f] || f)).join(", ");
            toast.success(`Contact "${contactMatch.name}" enrichi dans Sellsy (${labels})`);
          }
          if (r?.conflicts?.length) {
            setPhoneConflict({
              contactId: contactMatch.sellsy_id,
              contactName: contactMatch.name || "Contact",
              conflicts: r.conflicts,
              pendingExtracted: {
                mobile_phone: data?.mobile_phone || null,
                landline_phone: data?.landline_phone || null,
                job_title: data?.job_title || null,
              },
            });
          }
        } catch (_) {
          // Silent: contact enrich is best-effort
        }
      }
    } catch (e) {
      console.error("CRM search error:", e);
    }
  };

  // Wrapper handlers: when a contact/company is created, update both
  // the persisted state AND the CRMStatusBanner so the Aperçu reflects the new Sellsy link.
  const handleContactCreated = (contact) => {
    setCreatedContact(contact);
    if (contact && contact.sellsy_id) {
      setCrmStatus(prev => ({
        ...prev,
        contact: {
          id: contact.id,
          name: `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || contact.email,
          email: contact.email,
          sellsy_id: contact.sellsy_id,
          source: 'sellsy',
        }
      }));
    }
  };

  const handleCompanyCreated = (company) => {
    setCreatedCompany(company);
    if (company && company.sellsy_id) {
      setCrmStatus(prev => ({
        ...prev,
        company: {
          id: company.id,
          name: company.name,
          email: company.email,
          sellsy_id: company.sellsy_id,
          source: 'sellsy',
        }
      }));
    }
  };

  // Update extracted data
  const handleUpdateExtractedData = (field, value) => {
    setExtractedData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Refresh CRM status using the LATEST (possibly user-edited) extractedData.
  // Triggered by the "Re-vérifier" button in CRMStatusBanner.
  const handleRefreshCRMStatus = async () => {
    if (!extractedData) return;
    await searchCRM(extractedData);
    toast.success("Statut Sellsy actualisé");
  };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400 mx-auto mb-2" />
          <p className="text-sm text-slate-500">Chargement...</p>
        </div>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!user) {
    return <LoginPage />;
  }

  // Main app
  return (
    <>
      <SellsyStatusBanner apiUrl={API} />
      <div className={`app-container${isOutlookContext ? " outlook-mode" : ""}`} data-testid="app-container">
      {/* Email Input Panel */}
      <div className="email-preview" data-testid="email-input-panel">
        <div className="p-4 h-full flex flex-col">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-md bg-slate-900 flex items-center justify-center">
              <span className="text-white text-xs font-bold">A</span>
            </div>
            <div className="flex-1">
              <h2 className="text-sm font-semibold text-slate-900">Email entrant</h2>
              <p className="text-[10px] text-slate-400">
                {isOutlookContext
                  ? "Chargé automatiquement depuis Outlook"
                  : "Collez le contenu de l'email recu"}
              </p>
            </div>
            {isOutlookContext && (
              <button
                type="button"
                onClick={reloadOutlookEmail}
                className="text-[11px] px-2 py-1 rounded border border-slate-200 hover:bg-slate-100 text-slate-700"
                title="Recharger l'email courant depuis Outlook"
                data-testid="outlook-reload-btn"
              >
                ⟳ Recharger
              </button>
            )}
          </div>

          {/* Email paste area */}
          <textarea
            value={rawEmailText}
            onChange={(e) => setRawEmailText(e.target.value)}
            className="flex-1 w-full p-3 text-sm text-slate-700 bg-white border border-slate-200 rounded-md resize-none focus:outline-none focus:ring-1 focus:ring-slate-300 placeholder:text-slate-300 font-mono leading-relaxed"
            placeholder={`Collez ici le contenu de l'email...\n\nExemple de format :\n\nObjet: Demande de team building\nDe: marie.dupont@acme-corp.fr\nDate: 06/04/2026\n\nBonjour,\n\nJe souhaite organiser un evenement...\n\nCordialement,\nMarie Dupont`}
            data-testid="email-paste-area"
          />

          {/* Signature image paste zone */}
          <div
            className={`mt-2 p-3 border-2 border-dashed rounded-md text-center cursor-pointer transition-colors ${
              signatureImageB64
                ? 'border-emerald-300 bg-emerald-50'
                : 'border-slate-200 hover:border-slate-300 bg-slate-50'
            }`}
            onPaste={handleImagePaste}
            tabIndex={0}
            data-testid="signature-image-zone"
          >
            {signatureImageB64 ? (
              <div className="relative">
                <img
                  src={`data:image/png;base64,${signatureImageB64}`}
                  alt="Signature"
                  className="max-h-24 mx-auto rounded"
                />
                <button
                  onClick={() => setSignatureImageB64(null)}
                  className="absolute top-0 right-0 w-5 h-5 bg-rose-500 text-white rounded-full text-xs flex items-center justify-center hover:bg-rose-600"
                  data-testid="remove-signature-btn"
                >
                  x
                </button>
                <p className="text-[10px] text-emerald-600 mt-1">Signature image collee — sera analysee par l'IA</p>
              </div>
            ) : (
              <div>
                <p className="text-xs text-slate-400">Cliquez ici puis Ctrl+V pour coller une image de signature</p>
                <p className="text-[10px] text-slate-300 mt-0.5">L'IA extraira les infos de la signature (nom, tel, email...)</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sidebar */}
      <Sidebar
        email={email || { sender_email: '', sender_name: '', subject: '', body: '' }}
        rawEmailText={rawEmailText}
        extractedData={extractedData}
        loading={loading}
        crmStatus={crmStatus}
        animations={animations}
        templates={templates}
        onExtract={handleExtractEmail}
        onUpdateExtractedData={handleUpdateExtractedData}
        apiUrl={API}
        user={user}
        createdContact={createdContact}
        createdCompany={createdCompany}
        createdOpportunity={createdOpportunity}
        createdQuote={createdQuote}
        setCreatedContact={handleContactCreated}
        setCreatedCompany={handleCompanyCreated}
        setCreatedOpportunity={setCreatedOpportunity}
        setCreatedQuote={setCreatedQuote}
        isOutlookContext={isOutlookContext}
        reloadOutlookEmail={reloadOutlookEmail}
        openOutlookPopout={openOutlookPopout}
        onRefreshCRMStatus={handleRefreshCRMStatus}
        onInsertTrame={handleInsertTrame}
      />

      {phoneConflict && (
        <PhoneConflictDialog
          open={true}
          contactName={phoneConflict.contactName}
          conflicts={phoneConflict.conflicts}
          onCancel={() => setPhoneConflict(null)}
          onConfirm={async (overwriteFields) => {
            try {
              const r = await axios.post(
                `${API}/contacts/${phoneConflict.contactId}/enrich`,
                {
                  ...phoneConflict.pendingExtracted,
                  force_overwrite: overwriteFields,
                }
              );
              if (r.data?.status === "updated" && r.data?.updated_fields?.length) {
                const labels = r.data.updated_fields.map(f => ({
                  mobile_number: "Portable",
                  phone_number: "Fixe",
                }[f] || f)).join(", ");
                toast.success(`Contact "${phoneConflict.contactName}" mis à jour dans Sellsy (${labels})`);
              }
            } catch (e) {
              toast.error("Echec de la mise à jour du contact");
            } finally {
              setPhoneConflict(null);
            }
          }}
        />
      )}
      </div>
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <Toaster position="top-right" />
      <AppContent />
    </AuthProvider>
  );
}

export default App;
